import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Share2, Copy, Check, MessageCircle, Send, Facebook, Instagram, Sparkles, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { estimateCommission, fmtINR } from "../lib/commission";

/**
 * Floating share menu that includes referral tracking.
 * If the user is logged in, appends ?ref=<code> to the shared URL
 * so they earn 1% commission if the property converts.
 */
export default function PropertyShareMenu({ propertyTitle, propertyPrice, propertyId, propertyImage, referralEligible = true, iconOnly = false }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
        if (open) document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open]);

    const baseUrl = `${window.location.origin}/properties/${propertyId}`;
    const refCode = referralEligible ? user?.referral_code : null;
    const link = refCode ? `${baseUrl}?ref=${refCode}` : baseUrl;
    const reward = referralEligible && propertyPrice ? estimateCommission(propertyPrice) : null;
    const rewardAmount = reward?.amount || 0;

    const message = `🏡 *${propertyTitle}*${propertyPrice ? ` — ₹${propertyPrice.toLocaleString("en-IN")}` : ""}\n\nCheck out this property on Global Property Mart:\n${link}${refCode ? `\n\n(Earn up to 1% reward — referral code: ${refCode})` : ""}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            toast.success(refCode ? "Referral link copied — earn up to 1% reward!" : "Link copied");
            setTimeout(() => setCopied(false), 1800);
        } catch { toast.error("Could not copy"); }
    };

    // Detect mobile (touch device + smaller screen). Native share sheet works
    // best on mobile — opens the OS's built-in "Share to..." dialog with
    // WhatsApp/SMS/Gmail/etc. all in one place.
    const isMobile = typeof window !== "undefined" && (
        /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) ||
        window.matchMedia?.("(max-width: 767px)").matches
    );

    const onShareClick = async () => {
        // Try native share first (best mobile UX — opens system share sheet)
        if (isMobile && navigator.share) {
            try {
                await navigator.share({
                    title: propertyTitle || "Property on Global Property Mart",
                    text: message,
                    url: link,
                });
                if (refCode) {
                    toast.success("Shared! You'll earn up to 1% if this deal closes.");
                }
                return;
            } catch (e) {
                // User cancelled or error — fall through to dropdown menu
                if (e && e.name === "AbortError") return;
            }
        }
        // Desktop or no native share — show the dropdown menu
        setOpen((v) => !v);
    };

    const openShare = (url) => {
        // Use location.href on mobile so it opens the actual WhatsApp/Telegram app.
        // Use window.open on desktop so it opens in a new tab.
        if (isMobile) {
            window.location.href = url;
        } else {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <div className="relative inline-block" ref={menuRef}>
            {iconOnly ? (
                <button
                    type="button"
                    onClick={onShareClick}
                    aria-label="Share"
                    data-testid="btn-share"
                    className="w-10 h-10 rounded-full flex items-center justify-center text-[#002657] active:bg-slate-100"
                >
                    <Share2 className="w-5 h-5" />
                </button>
            ) : (
                <button
                    type="button"
                    onClick={onShareClick}
                    data-testid="btn-share"
                    className="px-3 py-2 rounded-md border border-white/10 text-white/80 hover:border-gold-500/40 text-sm flex items-center gap-1.5"
                >
                    <Share2 className="w-4 h-4" /> Share {refCode && <span className="text-[10px] uppercase tracking-wider text-gold-500 font-bold">& Earn 1%</span>}
                </button>
            )}
            {open && createPortal(
                <>
                    {/* Mobile: full-width bottom sheet | Desktop: overlay closable by outside click */}
                    <div className="fixed inset-0 bg-black/50 z-[9998]" onClick={() => setOpen(false)} data-testid="share-menu-backdrop" />
                    <div className="fixed left-0 right-0 bottom-0 md:left-1/2 md:right-auto md:bottom-auto md:top-24 md:-translate-x-1/2 md:w-96 rounded-t-2xl md:rounded-2xl bg-white shadow-[0_20px_60px_-15px_rgba(0,38,87,0.6)] z-[9999] overflow-hidden max-h-[85vh] flex flex-col" data-testid="share-menu">
                    {/* Header + close */}
                    <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-navy-500/8">
                        <div className="md:hidden w-10 h-1 rounded-full bg-navy-500/25 absolute left-1/2 -translate-x-1/2 -top-0 mt-1.5" />
                        <div className="font-heading text-lg font-bold text-navy-500">Share this property</div>
                        <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full hover:bg-navy-500/8 flex items-center justify-center text-navy-500/60" data-testid="share-menu-close">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="overflow-y-auto">
                    {refCode && rewardAmount > 0 && (
                        <div className="px-4 py-3 bg-gradient-to-r from-[#FFFBEF] to-white border-b border-gold-500/30">
                            <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold text-gold-700">
                                <Sparkles className="w-3 h-3" /> {reward.isRange ? `Earn up to ₹${rewardAmount.toLocaleString("en-IN")}` : `Earn ₹${rewardAmount.toLocaleString("en-IN")}`}
                            </div>
                            <div className="text-xs text-navy-500/70 mt-1">If this deal closes through your referral link.</div>
                        </div>
                    )}
                    <div className="p-2 space-y-1">
                        <ShareBtn
                            icon={MessageCircle}
                            label="WhatsApp"
                            color="#25D366"
                            onClick={() => {
                                // Use `whatsapp://send` on mobile so it opens the WhatsApp app directly.
                                // `wa.me` falls back to web if app not installed.
                                const url = isMobile
                                    ? `whatsapp://send?text=${encodeURIComponent(message)}`
                                    : `https://wa.me/?text=${encodeURIComponent(message)}`;
                                openShare(url);
                            }}
                            testid="share-whatsapp"
                        />
                        <ShareBtn
                            icon={Send}
                            label="Telegram"
                            color="#0088CC"
                            onClick={() => openShare(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(message)}`)}
                            testid="share-telegram"
                        />
                        <ShareBtn
                            icon={Facebook}
                            label="Facebook"
                            color="#1877F2"
                            onClick={() => openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`)}
                            testid="share-facebook"
                        />
                        <ShareBtn
                            icon={Instagram}
                            label="Instagram (copy link)"
                            color="#E4405F"
                            onClick={async () => { await navigator.clipboard.writeText(link); toast.success("Link copied — paste in your Instagram story or bio"); }}
                            testid="share-instagram"
                        />
                        <button
                            onClick={copy}
                            data-testid="share-copy"
                            className="w-full px-3 py-2.5 rounded-lg border border-navy-500/15 text-navy-500 font-semibold text-sm hover:bg-[#FAFAF7] flex items-center justify-between gap-2"
                        >
                            <span className="flex items-center gap-2">{copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}{copied ? "Copied!" : "Copy link"}</span>
                            <span className="text-[10px] uppercase tracking-wider text-navy-500/40 font-mono truncate max-w-[120px]">{link.replace(/^https?:\/\//, "")}</span>
                        </button>
                    </div>
                    {!user && (
                        <div className="px-4 py-3 bg-[#FAFAF7] border-t border-navy-500/8 text-xs text-navy-500/65">
                            <strong className="text-navy-500">Login</strong> to earn up to 1% reward when properties you share convert into deals.
                        </div>
                    )}
                    </div>
                </div>
                </>,
                document.body
            )}
        </div>
    );
}

function ShareBtn({ icon: Icon, label, color, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className="w-full px-3 py-2.5 rounded-lg text-navy-500 font-semibold text-sm hover:bg-[#FAFAF7] flex items-center gap-2 text-left"
        >
            <span className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: color, color: "#FFFFFF" }}>
                <Icon className="w-4 h-4 text-white" />
            </span>
            {label}
        </button>
    );
}
